import { useMemo, useState } from 'react'
import { match } from 'ts-pattern'
import {
  Text,
  Banner,
  Button,
  DatePickerField,
  GreyGrey80,
  GreyGrey90,
  Primary13Regular,
  Primary14SemiBold,
  SequenceMenuItem,
  Switch,
  Modal,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { formatISODate, toMoney, useDisclosure } from '@sequencehq/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useCreditNoteRevRecWarning } from 'CreditNotes/components/creditNoteRevRecWarning'
import { Invoice, ServerLineItem } from 'Invoices/types'
import { sumCurrentPayments } from 'Invoices/utils'
import {
  currentCreditNotes,
  currentPayments
} from 'Invoices/utils/currentlyApplied'
import { AddLineItem } from './AddLineItem'
import { EditLineItem } from './EditLineItem'
import { ApplyCreditOption, ApplyCreditSection } from './ApplyCreditSection'
import { InvoiceSection } from './InvoiceSection'
import { PreviouslyCreditedSection } from './PreviouslyCreditedSection'
import {
  CreditNoteLineItemRow,
  CreditNoteLineItemTable
} from './CreditNoteLineItemTable'
import { TotalsSection } from './TotalsSection'
import { getCreditNoteTotals } from './creditNoteTotals'
import {
  hasPartiallyAppliedLinkedCreditNote,
  overCreditedRows,
  remainingNetByLineItem
} from './remainingNetByLineItem'
import { seedRows } from './seedRows'
import { useIssueAndSendCreditNote } from './useIssueAndSendCreditNote'
import { useIssueAndApplyCreditNote } from './useIssueAndApplyCreditNote'
import { usePermissions } from 'lib/rbac'
import { useAccountingLockDateValidation } from 'common/components/SteppedModal/useAccountingLockDateValidation'

export const MenuItemIssueCreditNote = ({
  invoiceId,
  lockToRefund = false
}: {
  invoiceId: string
  lockToRefund?: boolean
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { can } = usePermissions()

  if (!can('org.creditNote:create')) {
    return null
  }

  return (
    <>
      <SequenceMenuItem
        uuid="issue-credit-note"
        label="Issue credit note"
        onClick={onOpen}
        data-testid="header.kebab-menu.issue-credit-note"
      />
      {/* Modal unmounts its children when closed, so DrawerBodyContent re-seeds
          its rows from the invoice each time the drawer is reopened. */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        variant="drawer"
        width="760px"
        data-testid="issue-credit-note.drawer"
      >
        <Modal.Header showCloseButton withBorder onClose={onClose}>
          Issue credit note
        </Modal.Header>
        <DrawerBodyContent
          invoiceId={invoiceId}
          lockToRefund={lockToRefund}
          onClose={onClose}
        />
      </Modal>
    </>
  )
}

/**
 * Loads the invoice data via queries (shared with the rest of this flow through
 * the cache) rather than the invoice editor's atoms, so the menu item only
 * depends on an invoiceId and can be mounted outside the editor.
 */
const DrawerBodyContent = ({
  invoiceId,
  lockToRefund,
  onClose
}: {
  invoiceId: string
  lockToRefund: boolean
  onClose: () => void
}) => {
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId },
    { staleTime: 1 * MINUTES }
  )
  const lineItemsQuery = useQuery(
    dashboardv99990101Client.getAllLineItems,
    { invoiceId },
    { staleTime: 1 * MINUTES }
  )

  const invoice = invoiceQuery.data
  const lineItems = lineItemsQuery.data?.items

  if (!invoice || !lineItems) {
    return null
  }

  return (
    <DrawerForm
      invoice={invoice}
      lineItems={lineItems}
      lockToRefund={lockToRefund}
      onClose={onClose}
    />
  )
}

const DrawerForm = ({
  invoice,
  lineItems,
  lockToRefund,
  onClose
}: {
  invoice: Invoice
  lineItems: ServerLineItem[]
  lockToRefund: boolean
  onClose: () => void
}) => {
  const flags = useFeatureFlags()
  const displayRevRecWarning = useCreditNoteRevRecWarning()
  const { currency, grossTotal } = invoice
  const appliedCreditNotes = useMemo(
    () => currentCreditNotes(invoice),
    [invoice]
  )
  const payments = useMemo(() => currentPayments(invoice), [invoice])
  const customerLegalName = invoice.customerLegalCompanyName
  const [accountingDate, setAccountingDate] = useState(() => new Date())
  const formattedAccountingDate = formatISODate(accountingDate)

  const { issueAndApply } = useIssueAndApplyCreditNote({
    invoiceId: invoice.id
  })
  const { issueAndSend } = useIssueAndSendCreditNote({ invoiceId: invoice.id })

  const [isLockGateOpen, setIsLockGateOpen] = useState(false)
  const lockDate = useAccountingLockDateValidation({
    entityName: 'credit note',
    entityDate: formattedAccountingDate,
    blockedTitle: "This credit note can't be issued",
    onClose: () => setIsLockGateOpen(false)
  })
  const isLockedOut =
    Boolean(lockDate.blocker) || lockDate.validation.length > 0

  // Payments already made to the invoice (served on the invoice), itemised in
  // the totals so the creditable figures reconcile against the original amount.
  const previouslyPaid = Number(sumCurrentPayments(payments))

  const [rows, setRows] = useState<CreditNoteLineItemRow[]>(() =>
    seedRows(
      lineItems,
      invoice.billingPeriod,
      appliedCreditNotes,
      invoice.paymentStatus === 'PARTIALLY_PAID' ||
        hasPartiallyAppliedLinkedCreditNote(appliedCreditNotes)
    )
  )
  const [applyCredit, setApplyCredit] = useState<ApplyCreditOption>(
    lockToRefund ? 'REFUND_MANUALLY' : 'ADJUST_AMOUNT_DUE'
  )
  // Each apply-credit path has its own confirmation: adjust-amount-due applies
  // the credit to the invoice, refund-manually issues and sends without it.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false)
  const [isAddLineItemOpen, setIsAddLineItemOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<CreditNoteLineItemRow | null>(
    null
  )
  const [validationTriggered, setValidationTriggered] = useState(false)

  // Selected rows missing rev-rec fields block issuing (mirrors the credit note
  // detail page). Seeded and manually added rows are pre-filled with defaults,
  // so this only catches rows whose service period couldn't be derived (e.g. an
  // invoice with neither line-item service periods nor a billing period).
  const incompleteLineItems = useMemo(() => {
    if (!flags.useRevRec) return []
    return rows.filter(
      row =>
        row.checked && (!row.servicePeriod || !row.revenueRecognitionMethod)
    )
  }, [flags.useRevRec, rows])
  const hasIncompleteItems = incompleteLineItems.length > 0

  // What the footer button does once validation passes, per the selected
  // apply-credit option. Both paths email the customer, so both ask for
  // confirmation; they differ only in whether the credit is applied to the
  // invoice (adjust-amount-due) or issued and sent on its own (refund-manually).
  const submitAction = match(applyCredit)
    .with('ADJUST_AMOUNT_DUE', () => ({
      label: 'Issue and send',
      onValid: () => setIsConfirmOpen(true)
    }))
    .with('REFUND_MANUALLY', () => ({
      label: 'Issue and send',
      onValid: () => setIsSendConfirmOpen(true)
    }))
    .exhaustive()

  const handleSubmitClick = () => {
    if (hasIncompleteItems) {
      setValidationTriggered(true)
      displayRevRecWarning({
        incompleteLineItems: incompleteLineItems.map(row => ({
          id: row.rowId,
          title: row.title,
          servicePeriod: row.servicePeriod,
          revenueRecognitionMethod: row.revenueRecognitionMethod
        })),
        onEdit: rowId =>
          setEditingRow(rows.find(row => row.rowId === rowId) ?? null),
        useRevRec: flags.useRevRec
      })
      return
    }

    // Dated before the accounting lock date -> resolve that first (blocker for
    // users who can't edit it, an inline move-lock-date step for those who can).
    if (isLockedOut) {
      setIsLockGateOpen(true)
      return
    }

    submitAction.onValid()
  }

  const creditedInvoice = {
    grossTotal,
    balance: invoice.balance,
    appliedCreditNotes,
    payments
  }

  // `total` is the gross credit (net + tax) actually issued and applied;
  // `exceedsCreditable` and `isZeroCredit` gate the "Issue and send" button.
  // All come from the same helper that drives TotalsSection, so the button and
  // the displayed adjusted amount stay in lockstep.
  const {
    total: creditTotal,
    exceedsCreditable,
    isZeroCredit,
    isNegativeCredit
  } = getCreditNoteTotals(rows, creditedInvoice)

  // Per-line-item ceiling: rows must not exceed what previous credit notes
  // left creditable on their invoice line item. Same arithmetic as seedRows,
  // so a freshly seeded drawer can never open in a blocked state.
  const remainingNet = useMemo(
    () => remainingNetByLineItem(lineItems, appliedCreditNotes),
    [lineItems, appliedCreditNotes]
  )
  const overCreditByRowId = useMemo(
    () => overCreditedRows(rows, remainingNet),
    [rows, remainingNet]
  )

  const adjustingAmountDue = applyCredit === 'ADJUST_AMOUNT_DUE'

  // The creditable ceiling (overall and per line item) only constrains the
  // adjust-amount-due path, where the credit reduces the invoice's balance. A
  // refund-manually credit note isn't applied to the invoice, so it isn't
  // capped by what's already been credited — only a zero-value or negative
  // credit is blocked there, both being rejected at finalisation.
  const issueDisabled =
    isZeroCredit ||
    isNegativeCredit ||
    lockDate.loading ||
    (adjustingAmountDue && (exceedsCreditable || overCreditByRowId.size > 0))

  return (
    <>
      <Modal.Content>
        <Flex flexDir="column" gap="20px">
          <InvoiceSection invoice={invoice} />
          <Flex flexDir="column" gap="16px">
            <DatePickerField
              label="Accounting date"
              value={accountingDate}
              onChange={newDate => newDate && setAccountingDate(newDate)}
              placeholder="Select date"
              width="100%"
              data-testid="issue-credit-note.accounting-date"
            />
            <ApplyCreditSection
              value={applyCredit}
              onChange={setApplyCredit}
              disabled={lockToRefund}
            />
          </Flex>
          <CreditNoteLineItemTable
            rows={rows}
            setRows={setRows}
            currency={currency}
            validationTriggered={validationTriggered}
            overCreditByRowId={
              adjustingAmountDue ? overCreditByRowId : new Map()
            }
            onAddLineItem={() => setIsAddLineItemOpen(true)}
            onEditLineItem={setEditingRow}
          />
          <PreviouslyCreditedSection
            appliedCreditNotes={creditedInvoice.appliedCreditNotes}
            currency={currency}
          />
          <TotalsSection
            rows={rows}
            currency={currency}
            invoice={creditedInvoice}
            appliesToInvoice={adjustingAmountDue}
            previouslyPaid={previouslyPaid}
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Modal.CancelButton data-testid="issue-credit-note.button.cancel" />
        <Button
          variant="primary"
          onClick={handleSubmitClick}
          disabled={issueDisabled}
          data-testid="issue-credit-note.button.issue-and-send"
        >
          {submitAction.label}
        </Button>
      </Modal.Footer>
      {isLockGateOpen && lockDate.blocker}
      {isLockGateOpen &&
        lockDate.validation.map(step => {
          const StepComponent = step.component

          return (
            <StepComponent
              key={step.stepKey}
              {...step.componentProps}
              onClose={() => setIsLockGateOpen(false)}
              onSubmit={async (newLockDate: string) => {
                // Move the lock date, then fall through to the existing confirm.
                await step.componentProps.onSubmit(newLockDate)
                setIsLockGateOpen(false)
                submitAction.onValid()
              }}
            />
          )
        })}
      <ConfirmIssueModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={async () => {
          const issued = await issueAndApply(rows, {
            accountingDate: formattedAccountingDate
          })

          // Issuing no longer navigates away (the success toast links to the
          // credit note instead), so the drawer closes itself on success.
          if (issued) {
            onClose()
          }

          return issued
        }}
        amount={toMoney({ currency, value: creditTotal.toString() })}
        customerName={customerLegalName}
      />
      <ConfirmIssueAndSendModal
        isOpen={isSendConfirmOpen}
        onClose={() => setIsSendConfirmOpen(false)}
        onConfirm={async generateCashCreditGrant => {
          const issued = await issueAndSend(rows, {
            accountingDate: formattedAccountingDate,
            generateCashCreditGrant
          })

          // Like issue-and-apply, the success toast links to the credit note,
          // so the drawer closes itself rather than navigating.
          if (issued) {
            onClose()
          }

          return issued
        }}
        amount={toMoney({ currency, value: creditTotal.toString() })}
        customerName={customerLegalName}
        customerEmails={invoice.customerEmails}
      />
      <AddLineItem
        isOpen={isAddLineItemOpen}
        onClose={() => setIsAddLineItemOpen(false)}
        currency={currency}
        defaultServicePeriod={invoice.billingPeriod}
        excludedProductIds={usedProductIds(rows)}
        onAdd={row => setRows(prev => [...prev, row])}
      />
      {editingRow && (
        <EditLineItem
          isOpen
          row={editingRow}
          currency={currency}
          excludedProductIds={usedProductIds(rows, editingRow.rowId)}
          onClose={() => setEditingRow(null)}
          onUpdate={updatedRow =>
            setRows(prev =>
              prev.map(row =>
                row.rowId === updatedRow.rowId ? updatedRow : row
              )
            )
          }
        />
      )}
    </>
  )
}

/**
 * Products already on a row, optionally ignoring one row (the edit flow ignores
 * the row being edited so its own product stays selectable). A product can only
 * appear on one credit note line item.
 */
const usedProductIds = (
  rows: CreditNoteLineItemRow[],
  excludeRowId?: string
): string[] =>
  rows
    .filter(row => row.rowId !== excludeRowId)
    .flatMap(row => (row.productId ? [row.productId] : []))

type ConfirmIssueModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<boolean>
  amount: string
  customerName: string
}

const ConfirmIssueModal = ({
  isOpen,
  onClose,
  onConfirm,
  amount,
  customerName
}: ConfirmIssueModalProps) => (
  <SimpleModal
    isOpen={isOpen}
    onClose={onClose}
    variant="dialog"
    title={`Issue credit note for ${amount} to ${customerName}?`}
    submitButtonText="Issue and send"
    submittingButtonText="Issuing..."
    onSubmit={onConfirm}
  >
    <Text>After issuing the original invoice will be adjusted</Text>
  </SimpleModal>
)

type ConfirmIssueAndSendModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (generateCashCreditGrant: boolean) => Promise<boolean>
  amount: string
  customerName: string
  customerEmails: string[]
}

/**
 * Confirmation for the refund-manually "Issue and send": issues and emails the
 * credit note without applying it to the invoice or ledger. Offers an opt-in
 * cash credit grant (mirrors the legacy credit-note issue dialog).
 */
const ConfirmIssueAndSendModal = ({
  isOpen,
  onClose,
  onConfirm,
  amount,
  customerName,
  customerEmails
}: ConfirmIssueAndSendModalProps) => {
  const [generateCashCreditGrant, setGenerateCashCreditGrant] = useState(false)

  // Reset the opt-in on close so a cancelled toggle can't carry into a later
  // submission; the modal stays mounted across opens within a drawer session.
  const handleClose = () => {
    setGenerateCashCreditGrant(false)
    onClose()
  }

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={handleClose}
      variant="dialog"
      title={`Issue credit note for ${amount} to ${customerName}?`}
      submitButtonText="Issue and send"
      submittingButtonText="Issuing..."
      onSubmit={() => onConfirm(generateCashCreditGrant)}
    >
      <Flex flexDir="column" gap="16px">
        <Text {...Primary13Regular} color={GreyGrey80}>
          Issue and send this credit note to <b>{customerName}</b>
          {customerEmails.length > 0 && ` (${customerEmails.join(', ')})`}. The
          invoice will not be adjusted, and after sending you can no longer edit
          the credit note.
        </Text>
        <Banner variant="neutral" withIcon={false}>
          <Flex flexDir="column" gap="8px">
            <Switch
              onChange={setGenerateCashCreditGrant}
              checked={generateCashCreditGrant}
              data-testid="issue-credit-note.cash-credit-toggle"
            >
              <Text {...Primary14SemiBold} color={GreyGrey90}>
                Add as cash credit
              </Text>
            </Switch>
            <Text {...Primary13Regular} color={GreyGrey80}>
              Creates a {amount} cash credit grant for <b>{customerName}</b>.
              Available credit is automatically applied to their future
              invoices.
            </Text>
          </Flex>
        </Banner>
      </Flex>
    </SimpleModal>
  )
}
